"use client";

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { fetchSiteSettings } from "../site-settings";

// The demo carries the editor's sheet code and a converted part: its own
// chunk, loaded after the page, never rendered on the server.
const RequestDemo = dynamic(() => import("./request-demo").then((module) => module.RequestDemo), {
  ssr: false,
  loading: () => (
    <div className="request-demo request-demo-pending" role="status">
      Loading the demo…
    </div>
  ),
});

/** The API's own reason, as a sentence, or `fallback`. */
async function reason(response: Response, fallback: string) {
  const detail = (await response.json().catch(() => null)) as { error?: string } | null;
  const text = detail?.error ?? "";
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}.` : fallback;
}

function BrandMark() {
  return (
    <span className="request-brand-mark" aria-hidden="true">
      ↗
    </span>
  );
}

export default function RequestPage() {
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [working, setWorking] = useState(false);
  const [requestsOpen, setRequestsOpen] = useState(true);

  useEffect(() => {
    let active = true;
    void fetchSiteSettings().then((settings) => {
      if (active && settings) setRequestsOpen(settings.access_requests_open);
    });
    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEmailError("");
    const email = new FormData(event.currentTarget).get("email");
    try {
      const response = await fetch("/api/auth/request-access", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok)
        throw new Error(await reason(response, "Could not save your request. Please try again."));
      setSubmitted(true);
    } catch (error) {
      setEmailError(error instanceof Error ? error.message : "Could not save your request.");
    }
  }

  async function handleCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setWorking(true);
    setCodeError("");
    try {
      const response = await fetch("/api/auth/redeem", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!response.ok)
        throw new Error(
          response.status === 429
            ? await reason(response, "Too many attempts. Try again later.")
            : "That invite is invalid, expired, or has reached its use limit.",
        );
      router.replace("/");
      router.refresh();
    } catch (error) {
      setCodeError(error instanceof Error ? error.message : "Could not redeem invite.");
      setWorking(false);
    }
  }

  return (
    <main className="request-page">
      <section className="request-shell">
        <div className="request-copy">
          <div className="request-brand">
            <BrandMark />
            <span>Specbound</span>
          </div>
          <div className="request-intro">
            <h1>
              From 3D models
              <br />
              to <span>2D drawings.</span>
            </h1>
            <p>
              Specbound imports your STEP files and draws the 2D views from the exact geometry:
              vector drawings in PDF, DWG and DXF.
            </p>
          </div>
          <div className="request-access-card">
            <div className="request-access-heading">
              <span className="request-lock" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path
                    d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v10H5z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div>
                <strong>Early access. By invite only.</strong>
                <p>
                  {requestsOpen
                    ? "Join the waiting list for early access and help shape what comes next."
                    : "The waiting list is closed for now. If you have an invite code, enter it below."}
                </p>
              </div>
            </div>
            {!requestsOpen ? null : submitted ? (
              <p className="request-success" role="status">
                Thanks — your request is on the list.
              </p>
            ) : (
              <form onSubmit={(event) => void handleSubmit(event)}>
                <label htmlFor="request-email">Email address</label>
                <input
                  id="request-email"
                  name="email"
                  type="email"
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
                <button type="submit">Request invite</button>
                {emailError && <p role="alert">{emailError}</p>}
              </form>
            )}
          </div>
          <p className="request-note">
            <a href="/login">Sign in with an account</a>
          </p>
          <div className="request-code">
            <span>Have an invite code?</span>
          </div>
          <form
            id="invite-code"
            className="request-invite-form"
            onSubmit={(event) => void handleCode(event)}
          >
            <label htmlFor="invite-input" className="sr-only">
              Invite code
            </label>
            <input
              id="invite-input"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="SB-XXXX-XXXX"
              autoComplete="off"
              required
            />
            <button type="submit" disabled={working}>
              {working ? "Checking…" : "Enter site"}
            </button>
            {codeError && <p role="alert">{codeError}</p>}
          </form>
          <p className="request-note">
            <span aria-hidden="true">♢</span> Your email is saved for the site owner to review; no
            mailing list.
          </p>
        </div>
        <div className="request-showcase">
          <RequestDemo />
          <div className="request-demo-notes">
            <p>
              <strong>Try it:</strong> click a view, then Auto dimension. This is the editor&apos;s
              own sheet code running in your browser on a real conversion: the views and true curves
              come from the exact engine, and Auto dimension measures holes, slots and edges from
              the model.
            </p>
            <p>
              In the app you upload your own STEP files, measure and annotate, cut sections, import
              a file&apos;s PMI, view the model in 3D and export PDF, DWG and DXF.
            </p>
            <p className="request-demo-credit">
              Sample part: test case FTC-10 from the NIST MBE PMI project (National Institute of
              Standards and Technology). Its use here implies no endorsement by NIST.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
